import React from 'react';
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Chip,
  Badge,
  Tooltip,
} from '@mui/material';
import { AlertTriangle, Clock } from 'lucide-react';
import { Case } from '../../../types';
import { useStatusColor } from '../../../hooks/useStatusColor';
import { TaskCounts } from '../../../types';
import { getTimeInStage } from '../../../utils/dates';

interface CaseCardProps {
  case_: Case;
  taskCounts: TaskCounts;
  onClick: () => void;
}

export const CaseCard: React.FC<CaseCardProps> = ({ case_, taskCounts, onClick }) => {
  const getStatusColor = useStatusColor();
  const timeInStage = getTimeInStage(case_.timeline, case_.currentStageId);

  // Calculate current stage task counts
  const currentStageTasks = case_.tasks.filter(task => 
    task.stageId === case_.currentStageId
  );
  
  const pendingCount = currentStageTasks.filter(task => 
    task.status !== 'completed' && new Date(task.dueDate) > new Date()
  ).length;
  
  const urgentCount = currentStageTasks.filter(task => 
    task.status !== 'completed' && new Date(task.dueDate) < new Date()
  ).length;

  const getTimeChipColor = (timeInStage: string): 'error' | 'info' => {
    const match = timeInStage.match(/(\d+)\s*(days?|hours?|minutes?|months?)/);
    if (!match) return 'info';

    const [, value, unit] = match;
    const numValue = parseInt(value, 10);

    if (unit.startsWith('month')) return 'error';
    if (unit.startsWith('day') && numValue >= 10) return 'error';
    return 'info';
  };

  return (
    <Card 
      sx={{ 
        mb: 2,
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:active': {
          transform: 'scale(0.98)',
        },
      }}
      onClick={onClick}
    >
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                mb: 0.5,
              }}
            >
              {case_.title}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {case_.caseNumber}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={case_.status}
              color={getStatusColor(case_.status)}
              size="small"
            />
            <Chip
              label={case_.practiceArea}
              variant="outlined"
              size="small"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={<Clock size={14} />}
              label={timeInStage}
              size="small"
              color={getTimeChipColor(timeInStage)}
              variant="outlined"
              sx={{
                '& .MuiChip-icon': {
                  ml: 1,
                  mr: 0.5,
                },
              }}
            />
          </Box>

          {(urgentCount > 0 || pendingCount > 0) && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {urgentCount > 0 && (
                <Tooltip title="Overdue tasks">
                  <Badge badgeContent={urgentCount} color="error">
                    <AlertTriangle size={18} />
                  </Badge>
                </Tooltip>
              )}
              {pendingCount > 0 && (
                <Tooltip title="Pending tasks">
                  <Badge badgeContent={pendingCount} color="warning">
                    <Clock size={18} />
                  </Badge>
                </Tooltip>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};